/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import dayjs from "dayjs";
import { useBusinessContextQuery } from "~/generated/graphql";
import { SortDirection } from "~/components";
import { dateFormat } from "~/constants";
import { useQueryState } from "~/hooks";
import { filterByName } from "~/tools";
import { SortFilter } from "../constants";
import { SearchParams } from "../OrganizationUnit/Lists";
import {
  mapOrgUnits,
  sortOrgUnits,
  buildDataTree,
  getOrgUnitWithItsParents,
} from "../utils";

export function useBusinessContext(id: string) {
  const today = dayjs();
  const currentYear = today.format(dateFormat.year);
  const currentMonth = today.month();

  const { data, loading, error, refetch } = useBusinessContextQuery({
    variables: {
      id,
      year: currentYear,
    },
  });
  const {
    state: {
      sortField = SortFilter.UnitName,
      sortDirection = SortDirection.Ascending,
      search,
    },
  } = useQueryState<SearchParams>();

  const units = useMemo(() => {
    const orgUnits = data?.businessContext?.orgUnits?.orgUnits ?? [];
    // Include parent org units in search
    const filteredOrgUnitsWithParents = [
      ...new Set(
        filterByName(orgUnits, search?.toString() ?? "").flatMap((item) =>
          getOrgUnitWithItsParents(item, orgUnits)
        )
      ),
    ];
    const mappedOrgUnits = mapOrgUnits(
      filteredOrgUnitsWithParents,
      currentMonth
    );
    const treeData = buildDataTree(mappedOrgUnits);

    return sortOrgUnits(treeData, sortField, sortDirection);
  }, [
    currentMonth,
    data?.businessContext?.orgUnits?.orgUnits,
    search,
    sortDirection,
    sortField,
  ]);

  return {
    data,
    loading,
    error,
    refetch,
    units,
  };
}
